<template>
  <div>
    <template v-if="generalStore.getFirebaseIntegration">
      <SharedSocialAuth @success="successAuth" />
      <div class="divider !text-white my-8">
        {{ $t("or") }}
      </div>
    </template>

    <form @submit.prevent="onSubmit">
      <div class="mb-4 w-full space-y-2">
        <div class="w-full">
          <UiInput
            v-model="signInForm.email"
            class="flex-1"
            name="email"
            :disabled="disabled"
            :placeholder="$t('enterYourEmail')"
            color="white"
          />
        </div>
        <div class="w-full">
          <UiInput
            v-model="signInForm.password"
            class="flex-1"
            name="password"
            :placeholder="$t('passwordMinimumCharacters')"
            type="password"
            :disabled="disabled"
            autocomplete="current-password"
            color="white"
          />
        </div>
        <p class="text-right mt-2">
          <NuxtLink
            class="ui-text-xs text-white underline underline-offset-2 hover:no-underline"
            :to="localePath('/reset-password')"
          >
            {{ $t("forgotPassword") }}
          </NuxtLink>
        </p>
      </div>
      <UiButton
        :loading="isSubmitting"
        type="submit"
        color="white"
        class="w-full"
      >
        {{ $t("signIn") }}
      </UiButton>
      <div class="text-center mt-4">
        <NuxtLink
          class="ui-text-xs text-white underline underline-offset-2 hover:no-underline"
          :to="localePath({ path: '/sign-up', query: route.query })"
        >
          {{ $t("createAnAccount") }}
        </NuxtLink>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { useForm } from "vee-validate";
import { object, string } from "yup";
import { SignInForm } from "ecom-types";
import { FetchError } from "ofetch";

const generalStore = useGeneralStore();
const route = useRoute();
const router = useRouter();
const localePath = useLocalePathPolyfill();
const { t } = useI18n();

defineProps<{
  disabled?: boolean;
}>();

const { onSignIn } = useAuth();

const initialSignInForm: SignInForm = {
  email: "",
  password: "",
};

const schema = object().shape({
  email: string().email().required().label(t("email2")),
  password: string().required().label(t("password")),
});

const signInForm = reactive(
  JSON.parse(JSON.stringify(initialSignInForm)),
) as SignInForm;

const {
  handleSubmit: handleSubmitSignIn,
  resetForm: resetFormSignIn,
  setErrors: setErrorsSignIn,
  isSubmitting,
} = useForm({
  initialValues: initialSignInForm,
  validationSchema: schema,
});

async function successAuth() {
  if (route.query.redirect) {
    await router.replace(localePath(route.query.redirect as string));
  } else {
    await router.replace(localePath("/profile"));
  }
}

const onSubmit = handleSubmitSignIn(async (values) => {
  try {
    await onSignIn(values);
    await Promise.all([generalStore.getCart(), generalStore.getWishList()]);
    resetFormSignIn();
    await successAuth();
  } catch (error) {
    if (error instanceof FetchError) {
      setErrorsSignIn(responseErrorToObjectErrors(error?.data?.errors));
    }
  }
});
</script>
